import { Box, createSvgIcon, SvgIcon, Typography } from '@mui/material';
import { DisparityWithCounts } from '../../types/Disparity/DisparityWithCounts.type';
import { capitalizeFirstLetter } from '../../utils/capitalizeFirstLetter';

const CLIENT_COLOR = '#764607';
const COMPARISON_COLOR = '#13507C';

interface PerCapitaChartProps {
  disparityData: DisparityWithCounts;
}

function PerCapitaChart({ disparityData }: PerCapitaChartProps) {
  const clientRawRate = disparityData.target_denominator
    ? disparityData.target_numerator / disparityData.target_denominator
    : 0;
  const comparisonRawRate = disparityData.comparison_denominator
    ? disparityData.comparison_numerator / disparityData.comparison_denominator
    : 0;

  const computeYAxisUnit = (r1: number, r2: number): number => {
    const positives = [r1, r2].filter((r) => r > 0);
    if (positives.length === 0) return 1;
    const minPositive = Math.min(...positives);
    const exponent = -Math.floor(Math.log10(minPositive));
    return Math.pow(10, exponent);
  };

  const perCapitaDenominator = computeYAxisUnit(clientRawRate, comparisonRawRate);

  const clientRate = clientRawRate * perCapitaDenominator;
  const comparisonRate = comparisonRawRate * perCapitaDenominator;

  const expectedComparisonCharges = clientRawRate * (disparityData.comparison_denominator || 0);
  const additionalCharges = Math.max(
    0,
    Math.round(expectedComparisonCharges - (disparityData.comparison_numerator || 0)),
  );

  const SVG_WIDTH = 1000;
  const SVG_HEIGHT = 460;

  const chartTopPadding = 30;
  const chartBottomPadding = 30;
  const chartHeight = SVG_HEIGHT - chartTopPadding - chartBottomPadding;

  const barWidth = 200;
  const barGap = 100;

  const bar1X = 120;
  const bar2X = bar1X + barWidth + barGap;

  const maxRate = Math.max(clientRate, comparisonRate);
  const yMaxRaw = maxRate * 1.2;

  const scaleValue = (value: number) => {
    if (value <= 0) return 1;
    const pow10 = Math.pow(10, Math.floor(Math.log10(value)));
    const n = value / pow10;
    const mult = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
    return mult * pow10;
  };
  const yMax = scaleValue(yMaxRaw || 1);
  const roughStep = yMax / 6;
  const step = scaleValue(roughStep / 1.00001);
  const yTicks: number[] = [];
  for (let v = 0; v <= yMax + 1e-9; v += step) {
    yTicks.push(Number(v.toFixed(6)));
  }
  if (yTicks[yTicks.length - 1] !== yMax) yTicks.push(yMax);

  const scale = (rate: number) => (rate / yMax) * chartHeight;

  const comparisonRaceDisplayName = disparityData.race.name.replace(
    'Non-client',
    `Non-${disparityData.case.client_race.name}`,
  );

  const STAGE_ACTION_WORDS: { [key: string]: string } = {
    Charging: 'charged',
    Arrest: 'arrested',
    Conviction: 'convicted',
    Sentencing: 'sentenced',
  };

  const getStagePastTenseActionWord = (stageName: string): string => {
    return STAGE_ACTION_WORDS[stageName] || stageName.toLowerCase();
  };

  const currentStageName = disparityData.stage.name;
  const stageActionWord = getStagePastTenseActionWord(currentStageName);

  const VizPersonIcon = createSvgIcon(
    <path d='M10.0205 6.45898C10.529 6.45898 10.9972 6.55746 11.4248 6.75391C12.6942 7.39285 13.0364 8.30704 13.0459 9.66113L12.2373 18.1426C11.9992 19.6253 9.90067 19.0063 9.85938 17.9268L9.84375 16.3545L8.6123 31.1602C8.42629 32.7173 7.05519 32.5431 6.36328 31.7227C5.64829 32.7452 4.14481 32.4653 4.09473 31.2578L3.06836 20.5889L3.06445 18.8779C2.37045 19.6704 0.813238 19.5314 0.779297 18.6475L0 9.96094C0.0240452 8.6648 0.498618 7.36628 1.62891 6.75391C1.99874 6.55741 2.44986 6.45899 2.98145 6.45898C5.17889 7.34555 7.63038 7.36982 9.84473 6.52637L10.0205 6.45898ZM6.4502 0C8.2497 0.000116176 9.70886 1.45844 9.70898 3.25781C9.70898 5.05734 8.24975 6.51652 6.4502 6.5166C4.6506 6.5166 3.19141 5.0574 3.19141 3.25781C3.19151 1.45837 4.6507 0 6.4502 0Z' />,
    'VizPersonIcon',
  );

  const VizChargeIcon = createSvgIcon(
    <path d='M23.999 24.7451C23.213 24.7451 22.5546 25.0299 22.0244 25.5986C21.4942 26.1676 21.2294 26.8509 21.2305 27.6475C21.2305 28.4717 21.4953 29.1611 22.0244 29.7158C22.5535 30.2705 23.2117 30.5486 23.999 30.5498V32.3467H4.48438C3.23758 32.3467 2.17843 31.8897 1.30762 30.9756C0.436995 30.0615 0.00114267 28.9527 0 27.6494V5.3916C4.51247e-07 3.90724 0.496332 2.63653 1.48828 1.58106C2.48016 0.525836 3.69826 -0.001192 5.14258 2.02429e-06H23.999V24.7451ZM4.48438 24.7441C3.69714 24.7441 3.03883 25.0286 2.50977 25.5986C1.9807 26.1688 1.71506 26.8521 1.71387 27.6475C1.71387 28.4717 1.97955 29.1611 2.50977 29.7158C3.04002 30.2705 3.70008 30.5486 4.48633 30.5498H20.5361C20.2105 30.1545 19.9593 29.716 19.7822 29.2344C19.6051 28.7552 19.5166 28.2254 19.5166 27.6455C19.5166 27.0982 19.6027 26.5767 19.7764 26.082C19.95 25.5875 20.2038 25.1417 20.5361 24.7441H4.48438ZM16.7998 8.73926C16.1547 8.73926 15.5786 8.9016 15.0723 9.22559C14.5658 9.54722 14.1662 10.0099 13.874 10.6123C13.5819 11.2121 13.4356 11.9304 13.4355 12.7676C13.4355 13.6023 13.5804 14.3214 13.8701 14.9238C14.1623 15.5261 14.562 15.9894 15.0684 16.3135C15.5748 16.635 16.1522 16.7959 16.7998 16.7959C17.211 16.7959 17.5905 16.732 17.9385 16.6045C18.2866 16.4769 18.596 16.2966 18.8662 16.0645C19.1389 15.8296 19.3623 15.5511 19.5352 15.2295C19.7104 14.9054 19.8288 14.5504 19.8896 14.165L18.75 14.1611C18.7037 14.4113 18.6198 14.6341 18.498 14.8281C18.3788 15.0194 18.2323 15.1808 18.0596 15.3135C17.8867 15.4462 17.6928 15.5473 17.4785 15.6162C17.2667 15.6826 17.0428 15.7158 16.8066 15.7158C16.3805 15.7158 15.9971 15.6035 15.6562 15.3789C15.3154 15.1543 15.0459 14.8228 14.8486 14.3838C14.6538 13.9447 14.5566 13.4057 14.5566 12.7676C14.5567 12.1346 14.6538 11.5982 14.8486 11.1592C15.0458 10.7203 15.314 10.3873 15.6523 10.1602C15.9932 9.93297 16.3796 9.81934 16.8105 9.81934C17.0491 9.81936 17.2745 9.85395 17.4863 9.92285C17.7005 9.99177 17.8936 10.0943 18.0664 10.2295C18.2392 10.3647 18.3856 10.5288 18.5049 10.7227C18.6241 10.9166 18.7062 11.1414 18.75 11.3965H19.8896C19.8264 10.9756 19.7044 10.6019 19.5244 10.2754C19.3442 9.9461 19.116 9.66762 18.8408 9.44043C18.5658 9.21084 18.2557 9.03733 17.9102 8.91992C17.5669 8.79999 17.1965 8.7393 16.7998 8.73926ZM7.09766 8.84668V16.6885H8.22656V13.9316H9.76074C10.3401 13.9316 10.8209 13.8207 11.2031 13.5986C11.5878 13.374 11.8755 13.0704 12.0654 12.6875C12.2577 12.3021 12.3535 11.8681 12.3535 11.3857C12.3535 10.9058 12.2583 10.4737 12.0684 10.0908C11.8784 9.70558 11.5921 9.40169 11.21 9.17969C10.8277 8.95775 10.3454 8.84668 9.76367 8.84668H7.09766ZM9.64355 9.86133C10.0183 9.86135 10.32 9.9266 10.5488 10.0566C10.7801 10.1868 10.9495 10.367 11.0566 10.5967C11.1638 10.8264 11.2178 11.0896 11.2178 11.3857C11.2177 11.6817 11.1637 11.9456 11.0566 12.1777C10.9495 12.41 10.7816 12.5942 10.5527 12.7295C10.3239 12.8622 10.0257 12.9287 9.6582 12.9287H8.22656V9.86133H9.64355Z' />,
    'VizChargeIcon',
  );

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '48px',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Typography sx={{ fontSize: '20px', fontWeight: 700 }}>
            Per-capita Disparity in {disparityData.stage.name} for {disparityData.subtitle}
          </Typography>
          <Typography sx={{ fontSize: '14px' }}>
            {disparityData.case.client_race.name} defendants are {stageActionWord}
            <span style={{ fontWeight: 700 }}>
              {' '}
              {disparityData.score !== undefined && disparityData.score >= 0.95 && disparityData.score <= 1.05
                ? 'approximately equally as often as'
                : `${disparityData.score?.toFixed(1)} times ${clientRate > comparisonRate ? 'more' : 'less'} often than`}
            </span>{' '}
            {comparisonRaceDisplayName} defendants for {disparityData.subtitle}, compared to the resident population.
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: CLIENT_COLOR, width: '96px' }}>
              {disparityData.case.client_race.name}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SvgIcon component={VizPersonIcon} viewBox='0 0 32 32' sx={{ color: CLIENT_COLOR }} />
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                  {disparityData.target_denominator.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SvgIcon component={VizChargeIcon} viewBox='0 0 32 32' sx={{ color: CLIENT_COLOR }} />
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                  {disparityData.target_numerator.toLocaleString()}
                </Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: CLIENT_COLOR }}>
                  &nbsp;{capitalizeFirstLetter(stageActionWord)}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: COMPARISON_COLOR, width: '96px' }}>
              {comparisonRaceDisplayName}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SvgIcon component={VizPersonIcon} viewBox='0 0 32 32' sx={{ color: COMPARISON_COLOR }} />
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                  {disparityData.comparison_denominator.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SvgIcon component={VizChargeIcon} viewBox='0 0 32 32' sx={{ color: COMPARISON_COLOR }} />
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                  {disparityData.comparison_numerator.toLocaleString()}
                </Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: COMPARISON_COLOR }}>
                  &nbsp;{capitalizeFirstLetter(stageActionWord)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex' }}>
        <svg width={SVG_WIDTH} height={SVG_HEIGHT}>
          {/* Y-axis line and ticks */}
          <line
            x1={50}
            y1={chartTopPadding}
            x2={50}
            y2={SVG_HEIGHT - chartBottomPadding}
            stroke='#000'
            strokeWidth={2}
          />
          <text
            x={10}
            y={SVG_HEIGHT / 2 + 20}
            fontSize={14}
            fill='#000'
            fontWeight={600}
            textAnchor='middle'
            style={{ transform: 'rotate(-90deg)', transformOrigin: '10px 250px' }}
          >
            Per-capita Rate per {perCapitaDenominator.toLocaleString()}
          </text>
          {yTicks.map((tick) => {
            const y = SVG_HEIGHT - chartBottomPadding - (tick / yMax) * chartHeight;
            return (
              <g key={tick}>
                <line x1={46} y1={y} x2={54} y2={y} stroke='#000' strokeWidth={2} />
                <text x={38} y={y + 4} fontSize={12} fill='#000' textAnchor='end'>
                  {tick}
                </text>
              </g>
            );
          })}
          {/* X-axis line */}
          <line
            x1={50}
            y1={SVG_HEIGHT - chartBottomPadding}
            x2={SVG_WIDTH - 30}
            y2={SVG_HEIGHT - chartBottomPadding}
            stroke='#000'
            strokeWidth={2}
          />
          {/* Client bar */}
          <rect
            x={bar1X}
            y={SVG_HEIGHT - chartBottomPadding - scale(clientRate)}
            width={barWidth}
            height={scale(clientRate)}
            fill={CLIENT_COLOR}
            rx={0}
          />
          <text
            x={bar1X + barWidth / 2}
            y={SVG_HEIGHT - chartBottomPadding - scale(clientRate) - 16}
            fontSize={16}
            fill={CLIENT_COLOR}
            fontWeight={700}
            textAnchor='middle'
          >
            {disparityData.case.client_race.name}
          </text>
          {/* Comparison bar */}
          <rect
            x={bar2X}
            y={SVG_HEIGHT - chartBottomPadding - scale(comparisonRate)}
            width={barWidth}
            height={scale(comparisonRate)}
            fill={COMPARISON_COLOR}
            rx={0}
          />
          <text
            x={bar2X + barWidth / 2}
            y={SVG_HEIGHT - chartBottomPadding - scale(comparisonRate) - 16}
            fontSize={16}
            fill={COMPARISON_COLOR}
            fontWeight={700}
            textAnchor='middle'
          >
            {comparisonRaceDisplayName}
          </text>
        </svg>
      </Box>
      <Typography variant='body2'>
        For every{' '}
        <b>
          {perCapitaDenominator.toLocaleString()} {disparityData.case.client_race.name} residents,{' '}
          {clientRate.toFixed(1)} defendants are {stageActionWord} with {disparityData.subtitle}
        </b>
        . Compare this with {comparisonRaceDisplayName} defendants, who are{' '}
        <b>
          {stageActionWord} at a rate of {comparisonRate.toFixed(1)} per {perCapitaDenominator.toLocaleString()}{' '}
          residents
        </b>
        . If {comparisonRaceDisplayName} defendants were {stageActionWord} at the same rate as{' '}
        {disparityData.case.client_race.name} defendants,{' '}
        <b>
          this would result in {additionalCharges.toLocaleString()} additional charges of {disparityData.subtitle} in
          this county.
        </b>
      </Typography>
    </Box>
  );
}

export default PerCapitaChart;
